import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Building2,
  MessageSquare,
  Wifi,
  WifiOff,
  RefreshCw,
  LogOut,
  Users,
  Activity,
  Settings,
  FileText,
  Loader2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [admin, setAdmin] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("adminToken");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const fetchDashboard = useCallback(
    async (showRefresh = false) => {
      if (showRefresh) setRefreshing(true);

      try {
        const response = await fetch(`${API_URL}/api/admin-panel/dashboard`, {
          headers: getAuthHeaders(),
        });

        if (response.status === 401) {
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminUser");
          navigate("/admin");
          return;
        }

        const data = await response.json();
        if (data.success) {
          setStats(data.data);
        }
      } catch (error) {
        console.error("Erro ao buscar dashboard:", error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [getAuthHeaders, navigate],
  );

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const adminUser = localStorage.getItem("adminUser");

    if (!token) {
      navigate("/admin");
      return;
    }

    if (adminUser) {
      setAdmin(JSON.parse(adminUser));
    }

    fetchDashboard();

    const interval = setInterval(() => fetchDashboard(), 30000);
    return () => clearInterval(interval);
  }, [navigate, fetchDashboard]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 sm:gap-4">
              <img
                src="/assets/logo.png"
                alt="Logo UP2"
                className="hidden sm:block h-14 w-auto drop-shadow-sm"
                onError={(e) => {
                  e.target.style.display = "none";
                  if (e.target.nextSibling)
                    e.target.nextSibling.style.display = "flex";
                }}
              />
              <img
                src="/assets/icone-logo.png"
                alt="Logo UP2"
                className="block sm:hidden h-8 w-auto drop-shadow-sm"
                onError={(e) => (e.target.style.display = "none")}
              />

              <div className="hidden w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl items-center justify-center shadow-sm">
                <Activity className="w-5 h-5 text-white" />
              </div>

              <div className="flex flex-col justify-center">
                <h1 className="text-base sm:text-lg font-bold text-slate-800 tracking-tight leading-tight">
                  WhatsApp Up2 Web
                </h1>
                <p className="hidden sm:block text-xs text-slate-500 font-medium">
                  Painel Administrativo
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden md:block text-sm text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full">
                Olá,{" "}
                <span className="text-blue-700 font-semibold">
                  {admin?.nome || "Admin"}
                </span>
              </div>

              <div className="flex items-center gap-1 sm:gap-2 border-l border-slate-200 pl-2 sm:pl-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/admin/settings")}
                  className="text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors w-9 h-9 sm:w-10 sm:h-10"
                  title="Configurações"
                  data-testid="admin-settings-button"
                >
                  <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-full transition-colors px-2 sm:px-3 h-9 sm:h-10"
                  title="Sair"
                  data-testid="admin-logout-button"
                >
                  <LogOut className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline font-medium">Sair</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-slate-800">Visão Geral</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchDashboard(true)}
            disabled={refreshing}
            className="border-slate-300 text-slate-600 hover:bg-slate-50"
            data-testid="refresh-dashboard-button"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            Atualizar
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Empresas */}
          <Card
            className="bg-white border-slate-200 shadow-sm"
            data-testid="stat-total-empresas"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Empresas</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1">
                    {stats?.empresas?.total || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Empresas Ativas */}
          <Card
            className="bg-white border-slate-200 shadow-sm"
            data-testid="stat-empresas-ativas"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Empresas Ativas</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">
                    {stats?.empresas?.ativas || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* WhatsApp Conectados - VERDE */}
          <Card
            className="bg-white border-slate-200 shadow-sm"
            data-testid="stat-whatsapp-conectados"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">WhatsApp Conectados</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">
                    {stats?.whatsapp?.conectados || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Wifi className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mensagens Hoje */}
          <Card
            className="bg-white border-slate-200 shadow-sm"
            data-testid="stat-mensagens-hoje"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Mensagens Hoje</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1">
                    {stats?.mensagens?.hoje || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <h2 className="text-xl font-semibold text-slate-800 mb-4">
          Ações Rápidas
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card
            className="bg-white border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
            onClick={() => navigate("/admin/empresas")}
            data-testid="nav-empresas"
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">
                    Gerenciar Empresas
                  </h3>
                  <p className="text-sm text-slate-500">
                    Cadastrar, editar e visualizar empresas
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            className="bg-white border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
            onClick={() => navigate("/admin/logs")}
            data-testid="nav-logs"
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">
                    Logs do Sistema
                  </h3>
                  <p className="text-sm text-slate-500">
                    Visualizar logs e erros
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            className="bg-white border-slate-200 hover:border-green-300 hover:shadow-md transition-all cursor-pointer"
            onClick={() => navigate("/admin/sessoes")}
            data-testid="nav-sessoes"
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Wifi className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">
                    Sessões WhatsApp
                  </h3>
                  <p className="text-sm text-slate-500">
                    Gerenciar conexões ativas
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-800 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                Resumo de Mensagens
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Total de mensagens</span>
                  <span className="text-slate-800 font-semibold">
                    {stats?.mensagens?.total?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Mensagens hoje</span>
                  <span className="text-blue-600 font-semibold">
                    {stats?.mensagens?.hoje?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-800 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Status das Empresas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Ativas</span>
                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200">
                    {stats?.empresas?.ativas || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Inativas/Bloqueadas</span>
                  <Badge className="bg-red-100 text-red-700 hover:bg-red-200">
                    {stats?.empresas?.inativas || 0}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
